import type { Diamond } from '../api/types'

/**
 * The three price states every buyer surface shares (signup redesign 2026-08):
 *   1. anon               → "Sign in for price"   (blurPrice prop, client auth state)
 *   2. signed-in unverified → lock + "Verification pending" (server sent null prices)
 *   3. verified           → $/ct bold + total sub-line (the original render)
 *
 * The server is authoritative for state 2 — priceUSD/pricePerCarat arrive
 * null with priceLocked:true, so there is nothing to leak client-side.
 */
export function PriceBlock({ stone, blurPrice, mult = 1, askaNote }: {
  stone: Diamond
  blurPrice?: boolean
  /** Fee multiplier (Aska Services mode). */
  mult?: number
  /** Extra note after the total, e.g. "incl. Aska". */
  askaNote?: string
}) {
  if (blurPrice) {
    return <p className="text-sm font-semibold text-gold-600">Sign in for price</p>
  }
  if (stone.priceUSD == null || stone.pricePerCarat == null) {
    return <LockedPrice />
  }
  const ppc = Math.round(stone.pricePerCarat * mult)
  const total = Math.round(stone.priceUSD * mult)
  return (
    <>
      {/* Ledger: the price is the headline number — set in IBM Plex Mono so it
          reads as an exchange quote, gold = value. Tracking tightened for mono. */}
      <p className="font-mono text-[17px] font-semibold text-gold-600 tabular tracking-[-0.01em]">
        ${ppc.toLocaleString()}<span className="text-[11px] font-medium text-navy-300">/ct</span>
      </p>
      <p className="font-mono text-[11px] text-navy-500 tabular">
        ${total.toLocaleString()} total{askaNote ? ` · ${askaNote}` : ''}
      </p>
    </>
  )
}

/** Signed-in-but-unverified price cell — calm, informative, no fake digits. */
export function LockedPrice({ compact }: { compact?: boolean }) {
  return (
    <div className="leading-tight" data-testid="price-locked">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-500">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
        Verification pending
      </p>
      {!compact && <p className="text-[11px] text-navy-300">Prices unlock once you're approved</p>}
    </div>
  )
}
